import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Bell, BellOff, X } from "lucide-react";
import { toast } from "sonner";

/**
 * PushOptIn — a discreet dismissible chip that appears when:
 *   - the user is authenticated
 *   - push isn't already granted/denied
 *   - the browser supports service workers + push
 *
 * Tapping it triggers `Notification.requestPermission()`, then subscribes
 * using the VAPID public key from the backend and POSTs the resulting
 * PushSubscription to `/push/subscribe`.
 */

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

const DISMISS_KEY = "aura-push-dismissed-at";
const REPROMPT_AFTER_DAYS = 30;

function suppressed() {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const age = (Date.now() - Number(raw)) / (1000 * 60 * 60 * 24);
  return age < REPROMPT_AFTER_DAYS;
}

function supported() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function PushOptIn() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!supported()) return;
    if (Notification.permission !== "default") return;
    if (suppressed()) return;
    const t = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(t);
  }, [user]);

  const enable = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setVisible(false);
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        return;
      }
      const { data: { public_key } } = await api.get("/push/vapid-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key),
      });
      const raw = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: raw.endpoint,
        keys: raw.keys,
      });
      toast.success("Notifications enabled");
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) {
      toast.error("Couldn't enable notifications: " + (e?.message || e));
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-4 right-4 bottom-24 z-40 sm:left-auto sm:right-4 sm:max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-300"
      role="dialog"
      aria-label="Enable notifications"
      data-testid="push-opt-in"
    >
      <div className="rounded-2xl bg-white border border-[rgba(33,59,42,0.10)] shadow-xl p-4 flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-forest/10 flex items-center justify-center shrink-0">
          <Bell className="h-5 w-5 text-forest" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-sage-500 font-semibold">
            Stay informed
          </div>
          <div className="text-sm text-stone-800 mt-1 leading-snug">
            Get a nudge when your doctor accepts, prescriptions are ready,
            or lab results release. No promos.
          </div>
          <button
            onClick={enable}
            className="mt-3 inline-flex items-center gap-2 h-9 px-4 rounded-full bg-forest text-cream text-sm font-medium hover:opacity-90 transition-opacity"
            data-testid="push-enable-btn"
          >
            Enable notifications
          </button>
        </div>
        <button
          onClick={dismiss}
          className="text-stone-400 hover:text-stone-700 transition-colors shrink-0"
          aria-label="Dismiss"
          data-testid="push-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * NotificationStatusBadge — tiny header pill showing current permission state.
 * Kept minimal so it doesn't fight for attention with the main navigation.
 */
export function NotificationStatusBadge() {
  const [perm, setPerm] = useState(() => (typeof Notification !== "undefined" ? Notification.permission : "unsupported"));
  useEffect(() => {
    if (typeof Notification === "undefined") return;
    const t = setInterval(() => setPerm(Notification.permission), 3000);
    return () => clearInterval(t);
  }, []);
  if (perm === "unsupported" || perm === "granted") return null;
  const Icon = perm === "denied" ? BellOff : Bell;
  return (
    <div className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-stone-400" data-testid="notification-status">
      <Icon className="h-3 w-3" /> {perm === "denied" ? "off" : "prompt"}
    </div>
  );
}
